// Cross-tab plumbing for the call pill: one BroadcastChannel per signed-in
// user (so a "Switch user" in another tab never mixes calls), the Do not
// disturb preference, and the desktop notification. Whether another tab is
// in front is asked over the channel (useCallPill), not by a storage
// heartbeat. No React here; useCallPill wires it up.

import { writeCallPushDnd } from '../../utils/callPushPrefs';

export const CHANNEL_PREFIX = 'dentalsem-call-pill';
export const DND_EVENT = 'call-pill-dnd';
export const dndKey = (userId) => `callPill:dnd:${userId ?? 'anon'}`;

const storage = () => {
    try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
};

// BroadcastChannel is missing in old Safari and in jsdom; without it each tab
// keeps its own pill, which is how the toast behaved before.
export const openChannel = (userId, Impl = typeof window !== 'undefined' ? window.BroadcastChannel : undefined) => {
    if (!userId || typeof Impl !== 'function') {
        return { post: () => {}, subscribe: () => () => {}, close: () => {} };
    }
    let channel;
    try { channel = new Impl(`${CHANNEL_PREFIX}:${userId}`); } catch {
        return { post: () => {}, subscribe: () => () => {}, close: () => {} };
    }
    return {
        post: (msg) => { try { channel.postMessage(msg); } catch { /* closed or uncloneable */ } },
        subscribe: (fn) => {
            const handler = (e) => { if (e?.data && typeof e.data === 'object') fn(e.data); };
            channel.addEventListener('message', handler);
            return () => channel.removeEventListener('message', handler);
        },
        close: () => { try { channel.close(); } catch { /* already closed */ } },
    };
};

export const readDnd = (userId) => {
    try { return storage()?.getItem(dndKey(userId)) === '1'; } catch { return false; }
};

// Other tabs hear the change through the `storage` event; this tab (the user
// menu and the pill are separate components) through DND_EVENT. The push service
// worker can't read localStorage, so it gets its own copy (callPushPrefs).
export const writeDnd = (userId, on) => {
    try { storage()?.setItem(dndKey(userId), on ? '1' : '0'); } catch { /* storage unavailable */ }
    try { window.dispatchEvent(new CustomEvent(DND_EVENT, { detail: { userId, on: Boolean(on) } })); } catch { /* no window */ }
    writeCallPushDnd(userId, on);
};

// ── Call sound (CU-869fmcc9r) ────────────────────────────────────────────────
// Per user: on ('1') once they press "Enable call sound" (or turn it on in the pill's
// menu), off ('0') when they turn it off; unset until they've been asked.
export const SOUND_EVENT = 'call-pill-sound';
export const soundKey = (userId) => `callPill:sound:${userId ?? 'anon'}`;

export const readSound = (userId) => {
    try { return storage()?.getItem(soundKey(userId)) ?? null; } catch { return null; }
};
export const soundOn = (userId) => readSound(userId) === '1';

export const writeSound = (userId, on) => {
    try { storage()?.setItem(soundKey(userId), on ? '1' : '0'); } catch { /* storage unavailable */ }
    try { window.dispatchEvent(new CustomEvent(SOUND_EVENT, { detail: { userId, on: Boolean(on) } })); } catch { /* no window */ }
};

// The one-time "Enable call sound" prompt: only while the user hasn't chosen yet.
export const shouldAskCallSound = (userId) => readSound(userId) === null;

// This tab is the one the user is looking at: visible and focused. A CRM
// window that is visible but behind another app still gets the notification.
export const isFrontTab = () => {
    if (typeof document === 'undefined' || document.hidden) return false;
    try { return typeof document.hasFocus !== 'function' || document.hasFocus(); } catch { return true; }
};

// Desktop notification for a ringing call; the caller decides it is needed
// (no CRM tab in front). The tag collapses the same call raised by several
// background tabs into one notification.
export const showDesktopNotification = ({ title, body, tag }, { onClick } = {}) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return null;
    if (window.Notification.permission !== 'granted') return null;
    try {
        const n = new window.Notification(title, {
            body, tag, renotify: false, requireInteraction: true, icon: '/favicon/android-chrome-512x512.png',
        });
        n.onclick = (event) => {
            event?.preventDefault?.();
            try { window.focus(); } catch { /* ignore */ }
            onClick?.();
            n.close();
        };
        return n;
    } catch {
        return null;
    }
};

// ── Ring sound (CU-869fmcc9r) ──────────────────────────────────────────────
// A short two-tone ring when a call starts ringing (call sound on, not on Do not disturb).
// Built with Web Audio (no sound file to load). Browsers only let a page play
// sound after the user has interacted with it, so the context is created on
// the first click/keypress (unlockRingTone) and reused when a call rings.
let audioContext = null;

const AudioContextImpl = () => (typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : undefined);

export const unlockRingTone = () => {
    try {
        const Impl = AudioContextImpl();
        if (!Impl) return;
        if (!audioContext) audioContext = new Impl();
        if (audioContext.state === 'suspended') audioContext.resume();
    } catch { /* no audio */ }
};

// Two short rings (~1.2 s in all). Returns false when no sound could play.
export const playRingTone = () => {
    try {
        if (!audioContext || audioContext.state !== 'running') return false;
        const ctx = audioContext;
        const start = ctx.currentTime + 0.02;
        [0, 0.6].forEach((offset) => {
            const gain = ctx.createGain();
            gain.gain.setValueAtTime(0.0001, start + offset);
            gain.gain.exponentialRampToValueAtTime(0.18, start + offset + 0.03);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.45);
            gain.connect(ctx.destination);
            [440, 480].forEach((freq) => {
                const osc = ctx.createOscillator();
                osc.frequency.value = freq;
                osc.connect(gain);
                osc.start(start + offset);
                osc.stop(start + offset + 0.5);
            });
        });
        return true;
    } catch {
        return false;
    }
};

// Whether we've already asked this user to allow desktop notifications for
// calls (asked once, with the reason; the browser remembers the answer).
export const notifyAskedKey = (userId) => `callPill:notifyAsked:${userId ?? 'anon'}`;

// The app's general "Enable browser notifications" prompt (EnableNotificationsDialog) sets
// this once shown. The call prompt waits for it, so the two never show together.
export const GENERAL_NOTIFY_PROMPT_KEY = 'notification-prompt-dismissed';

export const shouldAskNotificationPermission = (userId) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return false;
    if (window.Notification.permission !== 'default') return false;
    try {
        const s = storage();
        return s?.getItem(GENERAL_NOTIFY_PROMPT_KEY) === 'true' && s?.getItem(notifyAskedKey(userId)) !== '1';
    } catch { return false; }
};

// One ring per call however many CRM tabs are open in the background: the first tab that
// can play sound takes the call's ring key; the others stay quiet.
const RING_CLAIM_SETTLE_MS = 60;
export const ringKey = (callId) => `callPill:ring:${callId}`;
const tabId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const canPlayRing = () => Boolean(audioContext && audioContext.state === 'running');

export const claimRing = (callId, { settleMs = RING_CLAIM_SETTLE_MS } = {}) => new Promise((resolve) => {
    let s;
    try { s = storage(); } catch { s = null; }
    if (!s) { resolve(true); return; }
    try {
        if (s.getItem(ringKey(callId))) { resolve(false); return; }
        s.setItem(ringKey(callId), tabId);
    } catch { resolve(true); return; }
    // Two tabs can write at once: whoever's id is left after a moment rings.
    setTimeout(() => {
        try { resolve(s.getItem(ringKey(callId)) === tabId); } catch { resolve(true); }
    }, settleMs);
});

export const forgetRing = (callId) => {
    try { storage()?.removeItem(ringKey(callId)); } catch { /* storage unavailable */ }
};

export const markNotificationPermissionAsked = (userId) => {
    try { storage()?.setItem(notifyAskedKey(userId), '1'); } catch { /* storage unavailable */ }
};
